import { Component } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import   pointsWithinPolygon   from '@turf/points-within-polygon'; // brackets left off on purpose
import { MapService } from './map.service';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { UserMessageService } from './user-message.service';
import { SingleLegendComponent } from './single-legend.component';
import { DataLayerBaseComponent } from './data-layer-base.component';
import * as d3 from 'd3';
import { forkJoin, observable } from 'rxjs';
import { DataLayerGeojsonPolygonComponent } from './data-layer-geojson-polygon.component';

declare var L;


/*
 * data-layer-geojson-polygon-point defines a class to display geojson polygons, assigning a color based on a single station within each polygon.
 *
 * Within the configuration file, point stations can be included:
 * "overlayLayers": [
 *		{"id": "SoilMoisture", "label": "Soil Moisture", "basins": ["map/overlay-layers/basins.geojson"], 
			 "popupTitle": "NAME", "color": "brown", "weight": 3, "fillOpacity": "0.3", "stroke": true, 
			 "DWSType": "FractionMU6Hour,Last,1Day", "class": "data-layer-geojson-polygon-point", "range": [0.2, 0.4, 0.6, 0.8, 1.0], "refresh": false },
 * ...
 * ]
 * 
 *  If range is not specified, the novascore from the NovaStar station is applied to the basin.  Otherwise, the statistical value is used to determine the novascore
 *  of the basin.  The color of the basin is determined using the novascore, just like any point data.  NovaScores will be assigned to range values according to index.
 *
 * 			Even though this is exactly what has been added to other data-layer* classes, Angular is fighting back for only this class.  Removing for now.
			<app-single-legend [active]=active [legendUrl]=legendUrl></app-single-legend>
 */

@Component({
	selector: 'data-layer-geojson-polygon-point',
	template: `
		<div class="checkboxDiv">
			<label class="overlay-layer"><input type="checkbox" (click)="menuControlSelected($event)" />
{{htmlElementLabel}}
			</label>
		</div>
		`,
	styleUrls: ['app.component.css']
})
export class DataLayerGeojsonPolygonPointComponent extends DataLayerGeojsonPolygonComponent {

    basinFilenames: any[] = [];
    basinGeojsons: any[] = [];
    DWSType;
    DWSstatisticType;
	DWSduration;
	range = null;
	colors: any[] = [ '#008A36', '#F5B831', '#EE8818', '#B63D91', '#D12619' ];

	constructor(
        private http: HttpClient,
		mapService: MapService, dataLayerService: DataLayerService, configService: ConfigService, userMessageService: UserMessageService ) {
		super(mapService, dataLayerService, configService, userMessageService);
	}

	menuControlSelected (event): void {
		if ( event.target.checked ) {
            // first, read in boundary files if this is the first time this layer has been activated
            if ( this.basinGeojsons.length === 0 ) {
				const observableArray = [];
				console.log('basinFilenames', this.basinFilenames);
                for ( let i = 0; i < this.basinFilenames.length; i++ ) {
                    observableArray.push ( this.http.get ( this.configService.baseDir + this.basinFilenames[i], {responseType: 'text'} ));
                }
                const observableGeojsons = forkJoin(observableArray);
                observableGeojsons.subscribe ({
                    next: value => { 
                        for ( let i = 0; i < value.length; i++ ) {
                            // console.log('Value from observableGeojsons', value[i]);
                            this.basinGeojsons.push( JSON.parse ( <any>value[i] ));
						}
                    },
                    complete: () => { this.activateLayer();}
                })

            } else {
			    this.activateLayer();
            }
		} else {
			this.deactivateLayer();
		}

	}

	setData ( someData: any ) {
        super.setData(someData);
        
		if ( someData.hasOwnProperty('colors')) {
			// length of the colors array MUST be at least as many novascores as might occur
			if ( someData.colors.length >= 5 ) {
				this.colors = someData.colors;
			}
        }
		if ( someData.hasOwnProperty('basins')) {
			this.basinFilenames = someData.basins;
        }
		if ( someData.hasOwnProperty('range')) {
			this.range = someData.range;
        }
		if ( someData.hasOwnProperty('DWSType')) {
			const parts = someData.DWSType.split(',');
			
			this.DWSType = parts.length > 0 ? parts[0] : 'FractionMU';
			this.DWSstatisticType = parts.length > 1 ? parts[1] : 'Last';
			this.DWSduration = parts.length > 2 ? parts[2] : '1day';
        }
    }

	drawPolygons () {
		const self = this;
		this.mapLayer = L.geoJson(this.value, {
			// For now hard-code but could determine color from a property
			onEachFeature: this.onEachPolygonFeature,
			style: function (feature) {
				// console.log(feature);
				return { color: feature.novaScoreColor, fillColor: feature.novaScoreColor, weight: self.weight, opacity: self.opacity, fillOpacity: self.fillOpacity, stroke: self.stroke };
			},
			contents: this
		}, this).addTo(this.mapService.map);
	}

	// parseDataFile parses the fhit file given in the configuration  file to retrieve the fhit structures,
	// parses the DWS file for all MeanArealPrecip stations,
	// filters each according to basins given, and assigns the novascore from the DWS station to the fhit stations.
	parseDataFile () {
		console.log('In data-layer-geojson-polygon-point.parseDataFile.');
		const self = this;
		console.log(this.titleString);

				// get all the station summaries from DWS
				const stationSummariesURI = self.configService.createURI ( { 'filename': 'stationSummaries.geojson' });
                self.http.get ( stationSummariesURI ).subscribe ( allStations => {

					console.log(allStations);
					// filter the features to only include those containing dataTypes of DWSType such as "FractionMU"
					// Should that be in the configuration file rather than hard-coded?
					allStations['features'] = allStations['features'].filter(function(s) {
						const dt = s.properties.stationSummary.dataTypes;
						const dt_filtered = dt.filter(function (d) { return d.name === self.DWSType });
						return dt_filtered.length === 0 ? false : true; });

					// At this point, allStations.features contains only DWSType stations
					// Need to set fhit station novascores based on DWS DWSType station in the same basin
					// console.log('Number of basin files: ' + self.basinGeojsons.length );
					const basins = [];
                	for ( let i = 0; i < self.basinGeojsons.length; i++) {
						const basinGeojson = self.basinGeojsons[i];
						// console.log( allFHITStations );
						// console.log( basinGeojson );

						for ( let f = 0; f < basinGeojson.features.length; f++ ) {

							const singleBasin = basinGeojson.features[f];

							// allStationsFiltered are all the stations within the current basin
							// console.log(allStations);
							const allStationsFiltered = pointsWithinPolygon( <any>allStations, singleBasin );
							// console.log('Special station first', allStationsFiltered);

							if ( allStationsFiltered.features.length > 0 ) {
								const tmpStatistic = allStationsFiltered.features[0].properties.stationSummary.statistics.filter(function(stat) {
									return stat.dataType === self.DWSType && stat.statisticType === self.DWSstatisticType && stat.duration.toUpperCase() === self.DWSduration.toUpperCase();
								});
								// console.log('Special station', allStationsFiltered.features[0].properties.stationSummary.name, allStationsFiltered);

								if ( tmpStatistic.length > 0 ) {
									const s = tmpStatistic[0];
									if ( s.statisticValue != null ) {

										// console.log( 'Found statistic value', s );
										// if range wasn't specified, simply use the novascore of s; otherwise, assign based on values in range
										if ( this.range == null ) {
											singleBasin.novaScore = s.novaScore;
										} else {
											for ( let k = 0; k < this.range.length; k++ ) {
												if ( s.statisticValue <= this.range[k] ) {
													singleBasin.novaScore = k + 1;  // novascore is based on the index of the range value
													k = this.range.length;	// end loop; value has been found
												}
											}
										}
										console.log('Setting point novascore to ' + singleBasin.novaScore);
										singleBasin.novaScoreColor = this.colors[singleBasin.novaScore-1];
										basins.push(singleBasin);
									} else {
										// console.log( 'Could not use because statistic value was null ', s );
									}
								}
							} else {
								// slows the display too much?
								// console.log( "No stations found within basin specified."); // need more descriptive location
							}
						}
					}

					self.value = basins;
					self.valueObservable.next(basins);
					console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' +
						stationSummariesURI + ' for all ' + self.id + ' stations was successfully completed.');
					// console.log ( dataLayer );
				});


	}
}
